<template>
  <div class="mobile-admin-dashboard" v-if="userType !== 'admin'">
    <disclosure
      @toggled="onToggle(tab, $event)"
      :key="`disclosure_${tab.slug}`"
      v-for="tab in tabs"
    >
      <template #label>
        <i class="mr-2 align-middle text-xl" :class="tab.iconClass" />
        <span class="align-middle capitalize">{{ tab.name }}</span>
      </template>
      <template #content v-if="getData(tab.slug) && getData(tab.slug).length">
        <bdg-list-view
          :items="getData(tab.slug)"
          :item-component="getListViewCell(tab.slug)"
          v-bind.sync="getListViewProps(tab.slug)"
        />
      </template>
    </disclosure>
  </div>
</template>

<script>
import Disclosure from "../../elements/Disclosure";
import ScheduledCrewCell from "../../elements/list-view/list-view-cells/dashboard-cells/ScheduledCrewCell";
import BdgListView from "../../elements/list-view/BdgListView";
import {
  CalendarTypes,
  DatetimeGroupByOptions,
  TableRowGroupTypes
} from "../../constants/enums";
import ProcurementCell from "../../elements/list-view/list-view-cells/dashboard-cells/ProcurementCell";
import TaskCell from "../../elements/list-view/list-view-cells/dashboard-cells/TaskCell";

export default {
  name: "MobileDashboardData",
  components: { BdgListView, Disclosure },
  data() {
    return {
      openTabs: []
    };
  },
  computed: {
    userType() {
      return this.$store.state.userType;
    },
    adminTabs() {
      if (this.$store.state.Dashboard.adminDashboardTabs) {
        return this.$store.state.Dashboard.adminDashboardTabs;
      }

      return [];
    },
    crewLeaderTabs() {
      if (this.$store.state.Dashboard.crewLeaderTabs) {
        return this.$store.state.Dashboard.crewLeaderTabs;
      }

      return [];
    },
    crewSupervisorDesignerTabs() {
      if (this.$store.state.Dashboard.crewSupervisorDesignerTabs) {
        return this.$store.state.Dashboard.crewSupervisorDesignerTabs;
      }

      return [];
    },
    tabs() {
      if (this.userType === "admin") {
        return this.adminTabs;
      } else if (this.userType === "crew_leader") {
        return this.crewLeaderTabs;
      } else {
        return this.crewSupervisorDesignerTabs;
      }
    },
    selectedCalendarType() {
      return this.$store.state.selectedCalendarType;
    },
    selectedDate() {
      return this.$store.state.selectedDate;
    },
    selectedWeekStartDate() {
      return this.$store.state.selectedWeekStartDate;
    },
    selectedMonthStartDate() {
      return this.$store.state.selectedMonthStartDate;
    },
    myTasks() {
      let tasks = this.$store.state.Dashboard.myTasks;
      if (tasks) {
        tasks.sort(
          (a, b) => a.scheduled_for.getTime() - b.scheduled_for.getTime()
        );
      }
      return tasks;
    },
    workOrders() {
      let workOrders = this.$store.state.Dashboard.workOrders;
      if (workOrders) {
        workOrders.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
            new Date(b.scheduled_for).getTime()
        );
      }
      return workOrders;
    },
    scheduledCrews() {
      const procurements = this.$store.state.Dashboard.scheduledCrews;
      if (procurements) {
        procurements.sort(
          (a, b) => a.scheduled_for.getTime() - b.scheduled_for.getTime()
        );

        return procurements.map(proc => {
          proc.scheduledForParams = {
            date: proc.scheduled_for,
            format: "hh:mm a"
          };
          return proc;
        });
      }
      return procurements;
    },
    scheduledDeliveries() {
      const procurements = this.$store.state.Dashboard.scheduledDeliveries;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.scheduled_for).getTime() -
            new Date(b.scheduled_for).getTime()
        );

        return procurements.map(proc => {
          if (proc.hasOwnProperty("notes")) {
            delete proc.notes;
          }

          proc.scheduledForParams = {
            date: proc.scheduled_for,
            format: "hh:mm a"
          };

          proc.displayTime = proc.scheduled_for;
          return proc;
        });
      }
      return procurements;
    },
    deliveredProcurements() {
      const procurements = this.$store.state.Dashboard.deliveredProcurements;
      if (procurements) {
        procurements.sort(
          (a, b) =>
            new Date(a.delivered_at).getTime() -
            new Date(b.delivered_at).getTime()
        );

        return procurements.map(proc => {
          if (proc.hasOwnProperty("notes")) {
            delete proc.notes;
          }

          proc.scheduled_time = this.$moment(proc.scheduled_for).format(
            "hh:mm a"
          );

          proc.displayTime = proc.delivered_at;

          return proc;
        });
      }
      return procurements;
    },
    onHoldDeliveries() {
      return this.$store.state.Dashboard.onHoldDeliveries;
    },
    cancelledDeliveries() {
      return this.$store.state.Dashboard.cancelledDeliveries;
    }
  },
  watch: {
    selectedCalendarType(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateData();
      }
    },
    selectedDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateData();
      }
    },
    selectedWeekStartDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateData();
      }
    },
    selectedMonthStartDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.updateData();
      }
    }
  },
  created() {
    if (this.tabs && this.tabs.length) {
      this.selectedTab = this.tabs[0];
    }
  },
  methods: {
    getData(slug) {
      switch (slug) {
        case "my-tasks":
          return this.myTasks;
        case "work-orders":
          return this.workOrders;
        case "crews-scheduled":
          return this.scheduledCrews;
        case "scheduled-deliveries":
          return this.scheduledDeliveries;
        case "delivered-procurements":
          return this.deliveredProcurements;
        case "on-hold-deliveries":
          return this.onHoldDeliveries;
        case "cancelled-deliveries":
          return this.cancelledDeliveries;
        default:
          return [];
      }
    },
    getUpdaterMethod(slug) {
      switch (slug) {
        case "my-tasks":
          return () => this.$store.dispatch("getMyTasks");
        case "work-orders":
          return () => this.$store.dispatch("getWorkOrders");
        case "crews-scheduled":
          return () => this.$store.dispatch("getScheduledCrews");
        case "scheduled-deliveries":
          return () => this.$store.dispatch("getScheduledDeliveries");
        case "delivered-procurements":
          return () => this.$store.dispatch("getDeliveredProcurements");
        case "on-hold-deliveries":
          return () => this.$store.dispatch("getOnHoldDeliveries");
        case "cancelled-deliveries":
          return () => this.$store.dispatch("getCancelledDeliveries");
        default:
          return [];
      }
    },
    getListViewCell(slug) {
      switch (slug) {
        case "my-tasks":
        case "work-orders":
          return TaskCell;
        case "crews-scheduled":
          return ScheduledCrewCell;
        case "scheduled-deliveries":
        case "delivered-procurements":
        case "on-hold-deliveries":
        case "cancelled-deliveries":
          return ProcurementCell;
        default:
          return null;
      }
    },
    getListViewProps(slug) {
      if (this.selectedCalendarType !== CalendarTypes.DAY) {
        switch (slug) {
          case "my-tasks":
          case "work-orders":
          case "crews-scheduled":
          case "scheduled-deliveries":
          case "on-hold-deliveries":
          case "cancelled-deliveries":
            return {
              areItemsGrouped: true,
              itemsGroupingParams: {
                groupType: TableRowGroupTypes.DATETIME,
                groupBy: DatetimeGroupByOptions.DAY,
                groupByProperty: "scheduled_for"
              }
            };
          case "delivered-procurements":
            return {
              areItemsGrouped: true,
              itemsGroupingParams: {
                groupType: TableRowGroupTypes.DATETIME,
                groupBy: DatetimeGroupByOptions.DAY,
                groupByProperty: "delivered_at"
              }
            };
          default:
            return undefined;
        }
      }
      return undefined;
    },
    updateData() {
      if (this.openTabs && this.openTabs.length) {
        this.openTabs.forEach(slug => {
          this.getUpdaterMethod(slug)();
        });
      }
    },
    onToggle(tab, isOpen) {
      if (isOpen && this.openTabs.indexOf(tab.slug) === -1) {
        this.openTabs.push(tab.slug);
        this.getUpdaterMethod(tab.slug)();
      } else if (!isOpen && this.openTabs.indexOf(tab.slug) > -1) {
        this.openTabs.splice(this.openTabs.indexOf(tab.slug), 1);
      }
    }
  }
};
</script>
